import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { accountQuery, accountTopEventIds, topEventsQuery } from '@/api';
import { selectOutcomeForEvent, useBetslipTicket, withBetslipSelectedState } from '@/betslip';
import { mapEventToWidgetProps } from '@/domain/event-row';
import { formatOutcomeLabel } from '@/domain/event-details';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { translateMarketLabel } from '@/i18n/market-labels';
import { translateOutcomeLabel } from '@/i18n/outcome-labels';
import { cn } from '@/lib/cn';
import { toSlug } from '@/shell/sports-menu-utils';
import { HorizontalScrollRow, Skeleton } from '@/ui/common';
import { EventWidget } from '@/ui/sports/EventWidget';

export interface HomeTopEventsProps {
  className?: string;
  /** Sport name sent to topevents. Defaults to Football (home). */
  sport?: string;
  /**
   * When true (default), include Account.TopEvents as EventIds.
   * Sports landing passes false so the API returns sport-scoped tops.
   */
  includeAccountEventIds?: boolean;
  /** Show the “See all” link. Defaults to true. */
  showSeeAll?: boolean;
}

/** Figma Top Events Slider — horizontal rail of featured EventWidgets. */
export function HomeTopEvents({
  className,
  sport = 'Football',
  includeAccountEventIds = true,
  showSeeAll = true,
}: HomeTopEventsProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { selectedLanguage, isLoadingTranslations } = useTranslations();
  const { data: account } = useQuery(accountQuery());
  const feedType = account?.FeedType;
  const betslipTicket = useBetslipTicket();
  const accountEventIds = useMemo(() => accountTopEventIds(account), [account]);
  const eventIds = includeAccountEventIds ? accountEventIds : [];
  const topEventsLabel = t('Topevents', { defaultValue: 'Top Events' });

  const query = useQuery({
    ...topEventsQuery({ Sport: sport, EventIds: eventIds }),
  });

  const resolveMarketLabel = useMemo(() => {
    void selectedLanguage;
    void isLoadingTranslations;
    return (key: string) => translateMarketLabel(key, i18n, feedType);
  }, [feedType, i18n, isLoadingTranslations, selectedLanguage]);

  const resolveOutcomeLabel = useMemo(() => {
    void selectedLanguage;
    void isLoadingTranslations;
    return (code: string, specifier?: string) =>
      formatOutcomeLabel(translateOutcomeLabel(code, i18n, feedType), specifier);
  }, [feedType, i18n, isLoadingTranslations, selectedLanguage]);

  if (query.isPending) {
    return (
      <section className={cn('flex flex-col gap-2', className)} aria-busy="true" aria-label={topEventsLabel}>
        <div className="flex items-center justify-between">
          <Skeleton kind="line" className="h-5 w-28" />
          {showSeeAll ? <Skeleton kind="line" className="h-4 w-14" /> : null}
        </div>
        <HorizontalScrollRow aria-label={topEventsLabel} withDesktopArrows className="gap-2 pl-0">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} kind="block" className="h-[200px] w-[336px] shrink-0 rounded-md" />
          ))}
        </HorizontalScrollRow>
      </section>
    );
  }

  const items = query.data ?? [];
  if (items.length === 0) return null;

  return (
    <section className={cn('flex flex-col gap-2', className)}>
      <header className="flex items-center justify-between overflow-clip">
        <h2 className="text-heading-m text-text-primary">{topEventsLabel}</h2>
        {showSeeAll ? (
          <Link
            to="/sports/$sport/topevents"
            params={{ sport: toSlug(sport) }}
            className="shrink-0 text-body-s tracking-wide text-status-info hover:underline"
          >
            {t('Seeall', { defaultValue: 'See all' })}
          </Link>
        ) : null}
      </header>

      <HorizontalScrollRow aria-label={topEventsLabel} withDesktopArrows className="gap-2 pl-0">
        {items.map(({ event, marketHeaders }) => {
          const widget = mapEventToWidgetProps(event, marketHeaders);
          return (
            <div key={event.EventId} role="listitem">
              <EventWidget
                {...widget}
                marketCode={resolveMarketLabel(widget.marketCode)}
                outcomes={withBetslipSelectedState(
                  betslipTicket,
                  event.EventId,
                  widget.outcomes.map((outcome) => ({
                    ...outcome,
                    outcome: resolveOutcomeLabel(outcome.outcome, outcome.specifier),
                  })),
                )}
                onSelectOutcome={(outcome) => selectOutcomeForEvent(event, outcome, account)}
                onOpenEvent={() => {
                  const eventSport = event.Sport ? toSlug(event.Sport) : undefined;
                  const category = event.Category ? toSlug(event.Category) : undefined;
                  const tournament = event.Tournament ? toSlug(event.Tournament) : undefined;
                  if (!eventSport || !category || !tournament) return;
                  void navigate({
                    to: '/sports/$sport/$category/$tournament/$eventId',
                    params: {
                      sport: eventSport,
                      category,
                      tournament,
                      eventId: String(event.EventId),
                    },
                    search: {},
                  });
                }}
              />
            </div>
          );
        })}
      </HorizontalScrollRow>
    </section>
  );
}
